import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSocket } from "@/context/SocketContext";
import GiftMenu from "@/components/GiftMenu";
import { GIFT_ICON, dollars } from "@/lib/giftIcons";
import { toast } from "sonner";
import { Radio, Gift, Target, Send, Video, VideoOff, Users, ArrowLeft } from "lucide-react";

const ICE = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
    { urls: "turn:openrelay.metered.ca:80", username: "openrelayproject", credential: "openrelayproject" },
    { urls: "turn:openrelay.metered.ca:443", username: "openrelayproject", credential: "openrelayproject" },
    { urls: "turn:openrelay.metered.ca:443?transport=tcp", username: "openrelayproject", credential: "openrelayproject" },
  ],
};

function Stream({ stream, muted, className }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current && stream) ref.current.srcObject = stream; }, [stream]);
  return <video ref={ref} autoPlay playsInline muted={muted} className={className} />;
}

function GoalBar({ goal }) {
  const pct = Math.min(100, Math.round((goal.progress / goal.amount) * 100));
  return (
    <div data-testid="live-goal-bar" className="bg-black/50 backdrop-blur-md border border-gold/30 rounded-2xl px-4 py-2.5">
      <div className="flex items-center justify-between gap-4 text-[11px] uppercase tracking-[0.15em]">
        <span className="text-gold flex items-center gap-1.5 truncate"><Target size={12} /> {goal.title}</span>
        <span className={goal.reached ? "text-emerald-400" : "text-text-main/80"} data-testid="live-goal-progress">
          {goal.reached ? "Goal reached" : `${dollars(goal.progress)} / ${dollars(goal.amount)}`}
        </span>
      </div>
      <div className="h-1.5 mt-2 rounded-full bg-white/10 overflow-hidden">
        <motion.div initial={false} animate={{ width: `${pct}%` }} transition={{ duration: 0.6 }}
          className={`h-full rounded-full ${goal.reached ? "bg-emerald-400" : "bg-gold"}`} />
      </div>
    </div>
  );
}

export default function LiveRoom() {
  const { id: creatorId } = useParams();
  const { user } = useAuth();
  const { send, connected, addSignalListener, addLiveListener, addGiftListener, addGoalListener } = useSocket();
  const isBroadcaster = user?.id === creatorId;

  const [creator, setCreator] = useState(null);
  const [live, setLive] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [chat, setChat] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [floats, setFloats] = useState([]);
  const [goal, setGoal] = useState(null);
  const [giftOpen, setGiftOpen] = useState(false);
  const [starting, setStarting] = useState(false);

  const localStreamRef = useRef(null);
  const pcsRef = useRef(new Map()); // broadcaster: viewerId -> RTCPeerConnection
  const pcRef = useRef(null); // viewer: single pc to broadcaster
  const chatEndRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get(`/live/${creatorId}`);
      setCreator(data.creator);
      setLive(data.live);
      setViewerCount(data.viewer_count);
    } catch {
      toast.error("Model not found");
    }
  }, [creatorId]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => {
    api.get(`/gifts/goal/${creatorId}`).then(({ data }) => setGoal(data.goal)).catch(() => {});
  }, [creatorId]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chat]);

  const offerToViewer = useCallback(async (viewerId) => {
    const pc = new RTCPeerConnection(ICE);
    pc.onicecandidate = (e) => {
      if (e.candidate) send({ type: "signal", to: viewerId, signal: { kind: "live-ice", candidate: e.candidate } });
    };
    pcsRef.current.set(viewerId, pc);
    const stream = localStreamRef.current;
    if (stream) stream.getTracks().forEach((t) => pc.addTrack(t, stream));
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    send({ type: "signal", to: viewerId, signal: { kind: "live-offer", offer } });
  }, [send]);

  const goLive = async () => {
    setStarting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      localStreamRef.current = stream;
      setLocalStream(stream);
      await api.post("/live/start");
      setLive(true);
      toast.success("You're live");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Camera/microphone unavailable");
    } finally {
      setStarting(false);
    }
  };

  const endLive = async () => {
    await api.post("/live/stop").catch(() => {});
    pcsRef.current.forEach((pc) => pc.close());
    pcsRef.current.clear();
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
    setLocalStream(null);
    setLive(false);
    setViewerCount(0);
    toast("Live stream ended");
  };

  // Viewer: announce join/leave to the room. Re-fires whenever the socket (re)connects
  // so a dropped/late connection doesn't leave the viewer stuck out of the room.
  useEffect(() => {
    if (isBroadcaster || !creatorId || !connected) return;
    send({ type: "live_join", creator_id: creatorId });
    return () => send({ type: "live_leave", creator_id: creatorId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBroadcaster, creatorId, connected]);

  useEffect(() => {
    if (isBroadcaster) return;
    return () => { pcRef.current?.close(); pcRef.current = null; };
  }, [isBroadcaster]);

  // WebRTC signaling (both roles share the generic `signal` relay, disambiguated by `kind`)
  useEffect(() => {
    const off = addSignalListener(async (data) => {
      const from = data.from;
      const sig = data.signal || {};
      if (isBroadcaster) {
        const pc = pcsRef.current.get(from);
        if (!pc) return;
        if (sig.kind === "live-answer") {
          await pc.setRemoteDescription(new RTCSessionDescription(sig.answer));
        } else if (sig.kind === "live-ice") {
          try { await pc.addIceCandidate(new RTCIceCandidate(sig.candidate)); } catch { /* ignore */ }
        }
      } else {
        if (sig.kind === "live-offer" && from === creatorId) {
          pcRef.current?.close();
          const pc = new RTCPeerConnection(ICE);
          pc.onicecandidate = (e) => {
            if (e.candidate) send({ type: "signal", to: creatorId, signal: { kind: "live-ice", candidate: e.candidate } });
          };
          pc.ontrack = (e) => setRemoteStream(e.streams[0]);
          pcRef.current = pc;
          await pc.setRemoteDescription(new RTCSessionDescription(sig.offer));
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          send({ type: "signal", to: creatorId, signal: { kind: "live-answer", answer } });
        } else if (sig.kind === "live-ice" && from === creatorId && pcRef.current) {
          try { await pcRef.current.addIceCandidate(new RTCIceCandidate(sig.candidate)); } catch { /* ignore */ }
        }
      }
    });
    return off;
  }, [addSignalListener, isBroadcaster, creatorId, send]);

  // Room lifecycle events + live chat
  useEffect(() => {
    const off = addLiveListener((data) => {
      if (data.type === "live_viewer_joined" && isBroadcaster) {
        offerToViewer(data.viewer_id);
      } else if (data.type === "live_viewer_left" && isBroadcaster) {
        pcsRef.current.get(data.viewer_id)?.close();
        pcsRef.current.delete(data.viewer_id);
      } else if (data.type === "live_viewer_count") {
        setViewerCount(data.count);
      } else if (data.type === "live_ended" && !isBroadcaster) {
        setRemoteStream(null);
        setLive(false);
        toast("The model ended their live stream");
      } else if (data.type === "live_chat") {
        setChat((c) => [...c.slice(-99), data]);
      }
    });
    return off;
  }, [addLiveListener, isBroadcaster, offerToViewer]);

  // Gifts + goal broadcasts (backend fans these out to every room viewer)
  useEffect(() => {
    const off = addGiftListener((data) => {
      const id = Date.now() + Math.random();
      setFloats((f) => [...f, { id, gift: data.gift, name: data.from_name }]);
      setTimeout(() => setFloats((f) => f.filter((x) => x.id !== id)), 2600);
    });
    return off;
  }, [addGiftListener]);

  useEffect(() => {
    const off = addGoalListener((data) => { if (data.user_id === creatorId) setGoal(data.goal); });
    return off;
  }, [addGoalListener, creatorId]);

  useEffect(() => () => {
    pcsRef.current.forEach((pc) => pc.close());
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  const sendChat = (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    send({ type: "live_chat", creator_id: creatorId, text: chatInput.trim() });
    setChatInput("");
  };

  if (!creator) return <div className="pt-32 text-center text-text-muted">Loading room…</div>;

  return (
    <div className="pt-20 pb-10 max-w-6xl mx-auto px-4 sm:px-6">
      <Link to="/models" data-testid="back-to-models" className="inline-flex items-center gap-2 text-xs text-text-muted hover:text-text-main transition-colors mt-4 mb-4">
        <ArrowLeft size={14} /> Velluto Models
      </Link>

      <div className="grid lg:grid-cols-[1fr_320px] gap-5">
        <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-black aspect-video">
          {isBroadcaster ? (
            localStream ? (
              <Stream stream={localStream} muted className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-4 text-text-muted">
                <Video size={32} strokeWidth={1.2} />
                <p className="text-sm">Your camera preview appears here once you go live</p>
              </div>
            )
          ) : remoteStream ? (
            <Stream stream={remoteStream} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-4 text-text-muted">
              <img src={creator.photo_url} alt={creator.display_name} className="w-20 h-20 rounded-full object-cover opacity-50" />
              <p data-testid="live-room-waiting" className="text-sm">
                {live ? "Connecting to the stream…" : `${creator.display_name} isn't live right now`}
              </p>
            </div>
          )}

          <div className="absolute top-4 left-4 flex items-center gap-2">
            {live && (
              <span data-testid="live-room-live-badge" className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white bg-red-600 px-3 py-1 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live
              </span>
            )}
            <span data-testid="live-room-viewer-count" className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-text-main bg-black/50 backdrop-blur-md px-3 py-1 rounded-full">
              <Users size={11} /> {viewerCount}
            </span>
          </div>

          {/* Floating gift animations */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {floats.map((f) => {
              const GI = GIFT_ICON[f.gift?.icon] || GIFT_ICON.heart;
              return (
                <motion.div key={f.id} initial={{ y: 0, opacity: 0, scale: 0.6 }}
                  animate={{ y: -220, opacity: [0, 1, 1, 0], scale: 1 }} transition={{ duration: 2.6, ease: "easeOut" }}
                  className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1">
                  <GI size={40} style={{ color: f.gift?.color }} />
                  <span className="text-xs text-white/90">{f.name}</span>
                </motion.div>
              );
            })}
          </div>

          {goal && (
            <div className="absolute bottom-4 left-4 right-4">
              <GoalBar goal={goal} />
            </div>
          )}
        </div>

        {/* Chat panel */}
        <div className="rounded-3xl border border-white/10 bg-surface flex flex-col h-[420px] lg:h-auto">
          <div className="px-5 py-4 border-b border-white/5">
            <p className="font-display text-xl leading-none">{creator.display_name}</p>
            {creator.tagline && <p className="text-xs text-text-muted mt-1">{creator.tagline}</p>}
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2.5" data-testid="live-chat-messages">
            {chat.length === 0 && <p className="text-xs text-text-muted">Say hello 👋</p>}
            {chat.map((c, i) => (
              <p key={i} className="text-sm leading-snug">
                <span className={`font-medium ${c.from === creatorId ? "text-gold" : "text-gold"}`}>{c.from_name}</span>
                <span className="text-text-main/90">: {c.text}</span>
              </p>
            ))}
            <div ref={chatEndRef} />
          </div>
          <form onSubmit={sendChat} className="flex items-center gap-2 p-3 border-t border-white/5">
            <input
              data-testid="live-chat-input"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Send a message…"
              maxLength={300}
              className="flex-1 bg-black/30 border border-white/10 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-wine/60"
            />
            <button data-testid="live-chat-send" type="submit" className="w-10 h-10 rounded-full bg-wine hover:bg-wine-hover transition-colors flex items-center justify-center shrink-0">
              <Send size={15} />
            </button>
          </form>
        </div>
      </div>

      <div className="flex items-center justify-center gap-3 mt-6">
        {isBroadcaster ? (
          live ? (
            <button data-testid="end-live-btn" onClick={endLive}
              className="flex items-center gap-2 px-8 py-3.5 rounded-full border border-red-500/50 text-red-400 hover:bg-red-500/10 transition-colors text-sm uppercase tracking-[0.2em]">
              <VideoOff size={15} /> End live stream
            </button>
          ) : (
            <button data-testid="go-live-btn" disabled={starting} onClick={goLive}
              className="flex items-center gap-2 px-8 py-3.5 rounded-full bg-wine hover:bg-wine-hover transition-colors text-sm uppercase tracking-[0.2em] disabled:opacity-50">
              <Radio size={15} /> {starting ? "Starting…" : "Go live"}
            </button>
          )
        ) : (
          <button data-testid="open-gift-menu" onClick={() => setGiftOpen(true)}
            className="flex items-center gap-2 px-8 py-3.5 rounded-full bg-gold text-ink hover:bg-gold/90 transition-colors text-sm uppercase tracking-[0.2em]">
            <Gift size={15} /> Send a gift
          </button>
        )}
      </div>

      <GiftMenu open={giftOpen} onClose={() => setGiftOpen(false)} toUserId={creatorId} toName={creator.display_name} context="live" />
    </div>
  );
}
